// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import '@fontsource/poppins/200.css';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import { ThemeProvider } from '@mui/material';
import {
  theme,
  StateProvider,
  DynamicDialogProvider,
  TranslationProvider,
  SnackbarProvider,
  BuildInfo,
  LoadingSplash,
} from '@shared/ui-kit';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router/dom';
import { PersistGate as ReduxPersistGate } from 'redux-persist/integration/react';
import { router } from '@client-app/routes';
import { appStore, persistor } from '@client-app/store';
import { AuthProvider } from '@shared/auth';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const container = document.getElementById('root');

const root = createRoot(container!);

root.render(
  <>
    <TranslationProvider>
      <StateProvider>
        <Provider store={appStore}>
          <LoadingSplash delay={500} />
          <SnackbarProvider>
            <ReduxPersistGate persistor={persistor} loading={null}>
              <ThemeProvider theme={theme}>
                <DynamicDialogProvider>
                  <AuthProvider onLogout={() => persistor.purge()}>
                    <Suspense fallback={null}>
                      <RouterProvider router={router} />
                    </Suspense>
                  </AuthProvider>
                </DynamicDialogProvider>
              </ThemeProvider>
            </ReduxPersistGate>
          </SnackbarProvider>
        </Provider>
      </StateProvider>
    </TranslationProvider>
    <BuildInfo />
  </>,
);
